/**
 * OpenAI streaming chunks → canonical chat events.
 *
 * Unlike the live bus, this path streams tool arguments genuinely
 * incrementally: a tool call arrives as an opening fragment carrying id + name,
 * then a run of fragments carrying argument text, closed only when
 * `finish_reason` lands. Mapping is therefore stateful, and the state is scoped
 * to one stream by the closure rather than living at module level.
 */
import type { ChatEvent } from '../../types/events';
import { EventType } from '../../types/events';

/** The slice of OpenAI's chunk shape this mapper reads. */
export interface OpenAIChunk {
  id?: string;
  model?: string;
  choices?: Array<{
    delta?: {
      role?: string;
      content?: string | null;
      tool_calls?: Array<{
        index: number;
        id?: string;
        function?: { name?: string; arguments?: string };
      }>;
    };
    finish_reason?: string | null;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
  } | null;
}

export interface OpenAIMapContext {
  threadId: string;
  runId: string;
}

export interface OpenAIChunkMapper {
  map(chunk: OpenAIChunk): ChatEvent[];
  /** Closes any still-open message/tool calls if the stream ends without a
   *  `finish_reason` — a dropped connection must not strand the UI mid-message. */
  finish(): ChatEvent[];
  error(message: string): ChatEvent[];
}

export function createOpenAIChunkMapper(ctx: OpenAIMapContext): OpenAIChunkMapper {
  let messageId = ctx.runId;
  let started = false;
  let ended = false;
  /** OpenAI keys tool-call fragments by `index`; the id only arrives on the first. */
  const openToolCalls = new Map<number, string>();
  let usage: { inputTokens?: number; outputTokens?: number } | undefined;

  function closeToolCalls(at: number): ChatEvent[] {
    const events: ChatEvent[] = [];
    for (const toolCallId of openToolCalls.values()) {
      events.push({ type: EventType.TOOL_CALL_END, timestamp: at, toolCallId });
    }
    openToolCalls.clear();
    return events;
  }

  function finishRun(at: number): ChatEvent[] {
    if (ended) return [];
    ended = true;
    const events: ChatEvent[] = [...closeToolCalls(at)];
    if (started) {
      events.push({ type: EventType.TEXT_MESSAGE_END, timestamp: at, messageId });
    }
    events.push({
      type: EventType.RUN_FINISHED,
      timestamp: at,
      threadId: ctx.threadId,
      runId: ctx.runId,
      outcome: { type: 'success' },
      ...(usage ? { result: { usage } } : {}),
    });
    return events;
  }

  return {
    map(chunk) {
      const at = Date.now();
      const events: ChatEvent[] = [];

      if (chunk.id) messageId = chunk.id;

      // Usage arrives on a final chunk that carries no choices; hold it so it can
      // ride out on RUN_FINISHED.
      if (chunk.usage) {
        usage = {
          inputTokens: chunk.usage.prompt_tokens,
          outputTokens: chunk.usage.completion_tokens,
        };
      }

      const choice = chunk.choices?.[0];
      if (!choice) return chunk.usage && ended ? [] : events;

      const delta = choice.delta;

      if (delta?.role && !started) {
        started = true;
        events.push({
          type: EventType.TEXT_MESSAGE_START,
          timestamp: at,
          messageId,
          role: 'assistant',
        });
      }

      if (delta?.content) {
        // A stream that emits content without ever declaring a role still has to
        // produce a well-formed START → CONTENT → END sequence.
        if (!started) {
          started = true;
          events.push({
            type: EventType.TEXT_MESSAGE_START,
            timestamp: at,
            messageId,
            role: 'assistant',
          });
        }
        events.push({
          type: EventType.TEXT_MESSAGE_CONTENT,
          timestamp: at,
          messageId,
          delta: delta.content,
        });
      }

      for (const fragment of delta?.tool_calls ?? []) {
        const known = openToolCalls.get(fragment.index);
        if (!known) {
          const toolCallId = fragment.id ?? `${messageId}:${fragment.index}`;
          openToolCalls.set(fragment.index, toolCallId);
          events.push({
            type: EventType.TOOL_CALL_START,
            timestamp: at,
            toolCallId,
            toolCallName: fragment.function?.name ?? '',
            parentMessageId: messageId,
          });
        }
        const args = fragment.function?.arguments;
        if (args) {
          events.push({
            type: EventType.TOOL_CALL_ARGS,
            timestamp: at,
            toolCallId: openToolCalls.get(fragment.index)!,
            delta: args,
          });
        }
      }

      if (choice.finish_reason) {
        events.push(...finishRun(at));
      }

      return events;
    },

    finish() {
      return finishRun(Date.now());
    },

    error(message) {
      ended = true;
      return [{ type: EventType.RUN_ERROR, timestamp: Date.now(), message }];
    },
  };
}
